import { useEffect, useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Loader2, MoreHorizontal, UserRoundPlus, Crown, Search, Trash, Lock } from "lucide-react"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/auth-store"
import { useBidsStore } from "@/stores/bids-store"
import { useBidMembersStore } from "@/stores/bid-members-store"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"

// ===================
// Types
// ===================

interface BidMember {
  id: string
  bidId: string
  userId: string
  role: "owner" | "contributor"
  email: string
  fullName: string | null
  avatarUrl: string | null
  createdAt: string
}

interface TeamSectionProps {
  bidId: string
  isReadOnly: boolean
}

// Sentinel value for the "Leave unassigned" option in the reassign picker.
// Radix Select cannot use an empty-string item value, so use a distinct token.
const REASSIGN_UNASSIGNED = "__unassigned__"

/** "A", "A and B", "A, B and C" */
function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ""
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
}

/**
 * What the transfer will do, in words. The caller may be an org admin rather
 * than the owner, so this names whoever owns the bid now instead of assuming
 * it is "you".
 */
function describeTransferOutcome(
  target: BidMember,
  members: BidMember[],
  currentUserId: string | undefined
): string {
  const targetName = target.fullName || target.email
  const isSelfTarget = target.userId === currentUserId
  const formerOwners = members.filter((m) => m.role === "owner" && m.userId !== target.userId)
  const callerIsFormerOwner = formerOwners.some((m) => m.userId === currentUserId)
  const formerOwnerNames = [
    ...(callerIsFormerOwner ? ["you"] : []),
    ...formerOwners.filter((m) => m.userId !== currentUserId).map((m) => m.fullName || m.email),
  ]

  if (formerOwnerNames.length === 0) {
    return `${isSelfTarget ? "You" : targetName} will become the owner.`
  }

  const former = formatNameList(formerOwnerNames)
  const becomeContributors = formerOwnerNames.length === 1 ? "a contributor" : "contributors"
  if (isSelfTarget) {
    return `You will become the owner and ${former} will become ${becomeContributors}.`
  }
  const formerCapitalised = former.charAt(0).toUpperCase() + former.slice(1)
  return `${formerCapitalised} will become ${becomeContributors} and ${targetName} will become the owner.`
}

// ===================
// Component
// ===================

export function TeamSection({ bidId, isReadOnly }: TeamSectionProps) {
  const currentUser = useAuthStore((s) => s.user)
  const orgRole = useAuthStore((s) => s.role)
  const { users: orgUsers, fetchUsers } = useBidsStore()
  const selectedBid = useBidsStore((s) => s.selectedBid)
  const setBidVisibility = useBidsStore((s) => s.setBidVisibility)
  // The bid menu's Team count and the header avatars read this store.
  const reloadBidMembers = useBidMembersStore((s) => s.load)
  const [visibilitySaving, setVisibilitySaving] = useState(false)

  const [members, setMembers] = useState<BidMember[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Dialog state
  const [removeTarget, setRemoveTarget] = useState<BidMember | null>(null)
  const [transferTarget, setTransferTarget] = useState<BidMember | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reassign-sections picker (remove dialog). Resets whenever a new remove
  // target is opened so a prior selection never leaks to the next removal.
  const [reassignToUserId, setReassignToUserId] = useState<string>(REASSIGN_UNASSIGNED)
  useEffect(() => {
    setReassignToUserId(REASSIGN_UNASSIGNED)
  }, [removeTarget?.userId])

  // Add member popover
  const [addOpen, setAddOpen] = useState(false)
  const [addSearch, setAddSearch] = useState("")

  const fetchMembers = useCallback(async () => {
    try {
      const data = await api.get<{ members: BidMember[] }>(`/bids/${bidId}/members`)
      setMembers(data.members)
    } catch {
      toast.error("Failed to load team members")
    } finally {
      setIsLoading(false)
    }
  }, [bidId])

  useEffect(() => {
    void fetchMembers()
    void fetchUsers()
  }, [fetchMembers, fetchUsers])

  const currentMember = members.find((m) => m.userId === currentUser?.id)
  const isOwner = currentMember?.role === "owner"
  // Org admins can manage any bid's team even if not in bid_members (covers pre-existing bids)
  const canManageTeam = isOwner || orgRole === "admin"

  const isRestricted = selectedBid?.visibility === "restricted"

  const handleToggleVisibility = async (checked: boolean) => {
    if (!selectedBid) return
    setVisibilitySaving(true)
    try {
      await setBidVisibility(selectedBid.id, checked ? "restricted" : "org")
      toast.success(checked ? "Bid restricted to members" : "Bid visible org-wide")
    } catch {
      toast.error("Failed to change visibility")
    } finally {
      setVisibilitySaving(false)
    }
  }

  // Add member
  const handleAddMember = async (userId: string) => {
    try {
      setIsSubmitting(true)
      await api.post(`/bids/${bidId}/members`, { userId, role: "contributor" })
      toast.success("Member added")
      setAddOpen(false)
      setAddSearch("")
      await fetchMembers()
      void reloadBidMembers(bidId)
    } catch {
      toast.error("Failed to add member")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Remove member
  const handleRemove = async () => {
    if (!removeTarget) return
    try {
      setIsSubmitting(true)
      const endpoint = `/bids/${bidId}/members/${removeTarget.userId}`
      if (reassignToUserId !== REASSIGN_UNASSIGNED) {
        await api.delete(endpoint, { reassignToUserId })
      } else {
        await api.delete(endpoint)
      }
      toast.success("Member removed")
      setRemoveTarget(null)
      await fetchMembers()
      void reloadBidMembers(bidId)
    } catch {
      toast.error("Failed to remove member")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Transfer ownership
  const handleTransfer = async () => {
    if (!transferTarget) return
    try {
      setIsSubmitting(true)
      await api.post(`/bids/${bidId}/transfer-ownership`, { newOwnerId: transferTarget.userId })
      toast.success(
        isTransferToSelf
          ? "You're now the owner."
          : `Ownership transferred to ${transferTarget.fullName || transferTarget.email}`
      )
      setTransferTarget(null)
      await fetchMembers()
      void reloadBidMembers(bidId)
    } catch {
      toast.error("Failed to transfer ownership")
    } finally {
      setIsSubmitting(false)
    }
  }

  const isTransferToSelf = !!transferTarget && transferTarget.userId === currentUser?.id

  // Candidates to hand the removed member's sections to: other remaining
  // members, excluding the member being removed and the current user.
  const reassignCandidates = members.filter(
    (m) => m.userId !== removeTarget?.userId && m.userId !== currentUser?.id
  )

  // Non-member org users for adding
  const memberUserIds = new Set(members.map((m) => m.userId))
  const availableUsers = orgUsers.filter(
    (u) => !memberUserIds.has(u.id)
  )
  const filteredAvailableUsers = availableUsers.filter((u) => {
    if (!addSearch) return true
    const q = addSearch.toLowerCase()
    return (u.fullName?.toLowerCase().includes(q) ?? false) || u.email.toLowerCase().includes(q)
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Visibility (owner/admin only) */}
      {canManageTeam && selectedBid && (
        <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/40 px-3 py-2.5">
          <div className="flex items-start gap-2.5">
            <Lock className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm">Restrict this bid</p>
              <p className="text-meta">
                Only members and org admins can see this bid
              </p>
            </div>
          </div>
          <Switch
            checked={isRestricted}
            disabled={visibilitySaving || isReadOnly}
            onCheckedChange={(checked) => void handleToggleVisibility(checked)}
            aria-label="Restrict this bid to members"
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between min-h-8">
        <h2 className="text-sm font-medium text-muted-foreground">
          Team Members ({members.length})
        </h2>
        {canManageTeam && !isReadOnly && (
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <UserRoundPlus className="h-4 w-4" />
                Add Member
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-0">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto p-1">
                {filteredAvailableUsers.length === 0 && (
                  <div className="px-3 py-4 text-center text-meta">
                    {availableUsers.length === 0 ? "All org members are already on this bid" : "No users found"}
                  </div>
                )}
                {filteredAvailableUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => void handleAddMember(user.id)}
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left hover:bg-muted transition-colors"
                  >
                    {(() => {
                      const color = getAvatarColor(user.id)
                      return (
                        <div className={cn("h-6 w-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden", user.avatarUrl ? "bg-muted" : color.bg)}>
                          {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt={user.fullName ?? "User"} className="h-6 w-6 rounded-full object-cover" />
                          ) : (
                            <span className={cn("text-[10px] font-medium", color.text)}>{getInitials(user.fullName)}</span>
                          )}
                        </div>
                      )
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{user.fullName || user.email}</div>
                      {user.fullName && (
                        <div className="text-meta truncate">{user.email}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Members List */}
      <div className="bg-card border rounded-lg divide-y">
        {members.map((member) => {
          const displayName = member.fullName || member.email
          const isSelf = member.userId === currentUser?.id
          const canEditRow = canManageTeam && !isReadOnly
          // Ownership can only go to someone who isn't already an owner. On your
          // own row this is an admin taking over the bid ("Make me owner").
          const canTransferTo = canEditRow && member.role !== "owner"
          const canRemove = canEditRow && !isSelf

          return (
            <div key={member.id} className="flex items-center gap-3 px-4 py-3">
              {/* Avatar */}
              {(() => {
                const color = getAvatarColor(member.userId)
                return (
                  <div className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden", member.avatarUrl ? "bg-muted" : color.bg)}>
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <span className={cn("text-xs font-medium", color.text)}>{getInitials(member.fullName)}</span>
                    )}
                  </div>
                )
              })()}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{displayName}</span>
                  {isSelf && (
                    <span className="text-meta">(you)</span>
                  )}
                </div>
                <div className="text-meta truncate">{member.email}</div>
              </div>

              {/* Role badge */}
              <Badge
                variant={member.role === "owner" ? "default" : "secondary"}
                className={cn(
                  "shrink-0",
                  member.role === "owner" && "gap-1"
                )}
              >
                {member.role === "owner" && <Crown className="h-3 w-3" />}
                {member.role === "owner" ? "Owner" : "Contributor"}
              </Badge>

              {/* Actions */}
              {(canTransferTo || canRemove) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canTransferTo && (
                      <DropdownMenuItem onClick={() => setTransferTarget(member)}>
                        <Crown className="h-4 w-4 mr-2" />
                        {isSelf ? "Make me owner" : "Transfer Ownership"}
                      </DropdownMenuItem>
                    )}
                    {canRemove && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setRemoveTarget(member)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )
        })}
      </div>

      {members.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserRoundPlus className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-base font-medium mb-1">No team members</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm">
            Add team members to collaborate on this bid.
          </p>
        </div>
      )}

      {/* Remove Confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-medium text-foreground">
                {removeTarget?.fullName || removeTarget?.email}
              </span>{" "}
              from this bid? They will lose access to all sections assigned to them.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {reassignCandidates.length > 0 && (
            <div className="space-y-1.5">
              <label htmlFor="reassign-sections" className="text-sm font-medium">
                Reassign their sections (optional)
              </label>
              <Select
                value={reassignToUserId}
                onValueChange={setReassignToUserId}
                disabled={isSubmitting}
              >
                <SelectTrigger id="reassign-sections">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={REASSIGN_UNASSIGNED}>Leave unassigned</SelectItem>
                  {reassignCandidates.map((candidate) => (
                    <SelectItem key={candidate.userId} value={candidate.userId}>
                      {candidate.fullName || candidate.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-meta">
                Their in-progress sections will be handed to this person. Leave
                unassigned to free them up.
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleRemove()}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Confirmation */}
      <AlertDialog open={!!transferTarget} onOpenChange={(open) => !open && setTransferTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer Ownership</AlertDialogTitle>
            <AlertDialogDescription>
              {isTransferToSelf ? (
                "Make yourself the owner of this bid?"
              ) : (
                <>
                  Transfer bid ownership to{" "}
                  <span className="font-medium text-foreground">
                    {transferTarget?.fullName || transferTarget?.email}
                  </span>
                  ?
                </>
              )}{" "}
              {transferTarget && describeTransferOutcome(transferTarget, members, currentUser?.id)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleTransfer()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
